import React, { useEffect, useMemo, useState } from "react";

import { IntegrationConfigurationFragment, PlanName } from "@/apollo/types";
import FormFactory from "@/components/modules/FormFactory/FormFactory";
import { classNames } from "@/helpers/classNames";
import { parsePlanName } from "@/features/billing";
import { PlanFeatureBadge } from "@/components/elements/PlanFeatureBadge";

type FormDisplayProps = {
  integration: IntegrationConfigurationFragment;
  isEditMode: boolean;
  onChangeForm: (formId: string) => void;
  currentFormId?: string | null;
};

type Form = IntegrationConfigurationFragment["forms"][0];

function removeNonEditableFields(form: Form): Form {
  return {
    ...form,
    fields: form.fields
      .map((f: any) => {
        if (f.type === "sub_form") {
          return removeNonEditableFields(f);
        }

        return f;
      })
      .filter((f: any) => "editable" in f && f.editable),
  };
}

export default function FormDisplay({
  integration,
  isEditMode,
  onChangeForm,
  currentFormId,
}: FormDisplayProps) {
  const formOptions = useMemo(() => {
    return integration.forms.map((form: Form) => {
      return isEditMode ? removeNonEditableFields(form) : form;
    });
  }, [integration, isEditMode]);

  // If there is a current form id (because it's stored), use that
  // If we are in Edit mode and there is no form id, it was set up before the feature, use the legacy form
  // If there is no legacy form, or we are not in edit mode, use the default form
  // Otherwise just use the first form
  const [selectedFormId, setSelectedFormId] = useState<string | undefined>(
    () => {
      if (currentFormId) {
        return currentFormId;
      }
      if (isEditMode) {
        const legacyForm = formOptions.find(
          (f: Record<any, string>) => f.legacy,
        );
        if (legacyForm) {
          return legacyForm.id;
        }
      }
      const defaultForm = formOptions.find(
        (f: Record<any, string>) => f.default,
      );
      if (defaultForm) {
        return defaultForm.id;
      }
      return formOptions[0]?.id;
    },
  );

  const lastAppliedFormIdRef = React.useRef<string | undefined>(undefined);

  useEffect(() => {
    if (selectedFormId && selectedFormId !== lastAppliedFormIdRef.current) {
      lastAppliedFormIdRef.current = selectedFormId;
      onChangeForm(selectedFormId);
    }
  }, [selectedFormId, onChangeForm]);

  const selectedForm = useMemo(() => {
    if (!selectedFormId) {
      return undefined;
    }
    return formOptions.find((choice: Form) => choice.id === selectedFormId);
  }, [formOptions, selectedFormId]);

  return (
    <div className="space-y-4">
      {formOptions.length > 1 && (
        <>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Select Authentication Method:
          </p>
          <fieldset>
            <legend className="sr-only">Configuration type</legend>
            <div className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
              {formOptions.map((choice: Form) => (
                <div
                  key={choice.id}
                  onClick={
                    choice.meetsPlanRequirement
                      ? () => {
                          setSelectedFormId(choice.id);
                        }
                      : () => {}
                  }
                  className={classNames(
                    "flex cursor-pointer items-center justify-between rounded-md border border-gray-300 p-4 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700",
                    !choice.meetsPlanRequirement && "pointer-events-none",
                  )}
                >
                  <div
                    className={classNames(
                      "flex items-center",
                      !choice.meetsPlanRequirement && "opacity-50",
                    )}
                  >
                    <input
                      id={`form-choice-tab-${choice.id}`}
                      name="form-choice-tab"
                      type="radio"
                      disabled={!choice.meetsPlanRequirement}
                      value={choice.id}
                      checked={selectedFormId === choice.id}
                      onChange={() => {
                        setSelectedFormId(choice.id);
                      }}
                      className="text-primary-600 focus:ring-primary-500 dark:text-primary-500 dark:focus:ring-primary-600 size-4 cursor-pointer border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    />
                    <label
                      htmlFor={`form-choice-tab-${choice.id}`}
                      className="ml-3 block cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-100"
                    >
                      {choice.formName}
                    </label>
                  </div>
                  {!choice.meetsPlanRequirement && choice.planRequirement && (
                    <PlanFeatureBadge
                      planName={
                        parsePlanName(choice.planRequirement) || PlanName.Custom
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </fieldset>
        </>
      )}
      {selectedForm && (
        <FormFactory form={selectedForm} editMode={isEditMode} />
      )}
    </div>
  );
}
